// Неликвиды склада — правила разбора и подписи. Чистые функции: ни React, ни сети.
//
// ОПРЕДЕЛЕНИЕ ЗДЕСЬ НЕ ЖИВЁТ. Возраст позиции и признак «неликвид» считает ЯДРО и отдаёт
// готовыми полями (`idle`, `approx`, `dead`) — ровно по формуле кабинета руководителя.
// Считать их ещё и на фронте значило бы завести вторую копию определения, а копии
// расходятся: у панели они уже разошлись бы с нами на один день из-за часового пояса
// браузера. Здесь только то, как это ПОКАЗАТЬ.

/** Позиция остатка с посчитанным возрастом (ответ /nl/items). */
export interface Позиция {
  stock_key: string
  wh: string
  nom: string
  wh_name: string | null
  region: string | null
  nom_name: string | null
  art: string | null
  qty: number | null
  /** Себестоимость без НДС. NULL — позиция заведена в 1С без неё. Это НЕ ноль. */
  cost: number | null
  unit_cost: number | null
  /** День, от которого считается возраст. */
  base_date: string | null
  idle: number
  /** Расхода не видели ни разу: возраст читается как «не меньше чем». */
  approx: boolean
  dead: boolean
  last_expense: string | null
  last_receipt: string | null
  first_receipt: string | null
}

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

export interface ИтогиНеликвида {
  positions: number
  with_cost: number
  no_cost: number
  cost: number
  qty: number
  approx_positions: number
  dead_positions: number
  dead_cost: number
  dead_no_cost: number
  max_idle: number | null
}

export interface КорзинаВозраста {
  bucket: string
  n: number
  cost: number
  approx_positions: number
}

export interface СрезРегиона {
  region: string
  positions: number
  cost: number
  dead_positions: number
  dead_cost: number
}

export interface СрезСклада extends СрезРегиона {
  wh: string
  wh_name: string
}

export interface Свод {
  days: number
  totals: ИтогиНеликвида
  dead_share_pct: number | null
  norm_share_pct: number
  by_age: КорзинаВозраста[]
  by_region: СрезРегиона[]
  by_warehouse: СрезСклада[]
  history_from: string
}

export interface СправочникиNL {
  counts: {
    positions: number
    no_cost: number
    cost: number
    warehouses: number
    regions: number
  }
  moves: {
    move_rows: number
    with_expense: number
    first_day: string | null
    last_day: string | null
  }
  regions: { region: string; positions: number; cost: number }[]
  warehouses: { wh: string; wh_name: string; region: string; positions: number; cost: number }[]
  freshness: Record<string, { entity: string; last_sync_at: string | null; last_count: number | null }>
  oldest: { entity: string; last_sync_at: string | null } | null
  history_from: string
  thresholds: number[]
  default_days: number
  norm_share_pct: number
  data_notes: Record<string, string>
}

/** Заказ клиента, под который держат резерв (ответ /nl/orders). */
export interface ЗаказПодРезерв {
  ref_key: string
  number: string | null
  doc_date: string | null
  status: string | null
  posted: boolean | null
  sum_doc: number | null
  deal_ref: string | null
  warehouse_ref: string | null
  contragent_ref: string | null
  manager_ref: string | null
  comment: string | null
  ship_plan_date: string | null
  age_days: number | null
}

/** Строка регистра «ТоварыКОтгрузке» — то, что реально держат под заказ. */
export interface СтрокаРезерва {
  Склад_Key?: string
  Номенклатура_Key?: string
  ДокументОтгрузки?: string
  ДокументОтгрузки_Type?: string
  ВРезервеBalance?: number | string
}

/** Резерв, свёрнутый по документу: под один заказ обычно держат несколько позиций. */
export interface РезервПоЗаказу {
  ref: string
  positions: number
  qty: number
  warehouses: string[]
}

/**
 * Свернуть строки регистра по документу отгрузки.
 *
 * Почему по документу, а не по позиции: названий номенклатуры в регистре нет, а тянуть их
 * из 1С по одной ссылке — это десятки запросов ради подписи. Вопрос, ради которого экран
 * и сделан, звучит «какой ЗАКАЗ держит склад и сколько он уже стоит», и на него хватает
 * свёртки. Разбор по позициям — отдельная задача и отдельная цена.
 *
 * Заказы, по которым ничего не зарезервировано (`ВРезервеBalance` = 0), отбрасываем:
 * регистр отдаёт и строки «к отгрузке», а держит склад именно резерв.
 */
export function резервПоЗаказам(строки: СтрокаРезерва[]): РезервПоЗаказу[] {
  const по: Record<string, РезервПоЗаказу> = {}
  for (const r of строки) {
    const кол = Number(r.ВРезервеBalance || 0)
    const док = r.ДокументОтгрузки
    if (!док || !(кол > 0)) continue
    const з = по[док] || (по[док] = { ref: док, positions: 0, qty: 0, warehouses: [] })
    з.positions += 1
    з.qty += кол
    const склад = r.Склад_Key
    if (склад && !з.warehouses.includes(склад)) з.warehouses.push(склад)
  }
  return Object.values(по)
}

/** Ссылки на заказы клиентов, порезанные на пачки: у ядра предел 60 ссылок на запрос. */
export const ПАЧКА_ССЫЛОК = 60

export function пачками<T>(список: T[], размер = ПАЧКА_ССЫЛОК): T[][] {
  const из: T[][] = []
  for (let i = 0; i < список.length; i += размер) из.push(список.slice(i, i + размер))
  return из
}

/**
 * Возраст позиции словами. «≥» — не украшение: расхода по этой позиции не видели ни разу,
 * и число снизу ограничено днём начала учёта в 1С, а не тем, сколько она реально лежит.
 */
export function возрастом(поз: { idle: number; approx: boolean }): string {
  return `${поз.approx ? '≥ ' : ''}${поз.idle}`
}

/** Подписи корзин возраста. Порядок задаёт ядро, здесь только слова. */
export const ПОДПИСЬ_ВОЗРАСТА: Record<string, string> = {
  '0-29': 'меньше месяца',
  '30-59': '1–2 месяца',
  '60-89': '2–3 месяца',
  '90-179': '3–6 месяцев',
  '180-364': 'полгода–год',
  '365+': 'больше года',
}

/** Порог в подписи кнопки. Панель пишет так же: «без расхода 90+ дней». */
export const подписьПорога = (d: number): string => `без движения ${d}+ дней`

/**
 * Оговорки, которые экран показывает ВСЕГДА, а не по кнопке «подробнее». Каждая из них —
 * причина, по которой число на экране можно прочитать неверно; спрятать такую причину
 * значит соврать аккуратной вёрсткой.
 */
export const ВАЖНЫЕ_ОГОВОРКИ = [
  'same_as_panel', 'no_history', 'no_move_row', 'threshold_cliff', 'no_cost',
  'dates_are_windows', 'no_reserve', 'positive_only',
] as const

/**
 * Свежесть зеркала одной строкой. Берём САМЫЙ СТАРЫЙ из двух синков: остатки без движений
 * (или наоборот) дают неверный возраст, и «свежо» здесь — это когда свежи обе таблицы.
 */
export function свежесть(с?: СправочникиNL['oldest']): { minutes: number } | null {
  if (!с?.last_sync_at) return null
  const t = Date.parse(с.last_sync_at)
  if (Number.isNaN(t)) return null
  return { minutes: Math.max(0, Math.round((Date.now() - t) / 60000)) }
}

/**
 * Доля неликвида в норме? Норму («≤ 15 %») задаёт ядро тем же числом, что и панель.
 * Отдельная функция, а не сравнение на месте: сравнений на экране три, и разъехаться
 * им нельзя.
 */
export const вНорме = (доля: number | null, норма: number): boolean =>
  доля !== null && доля <= норма

/**
 * Сколько денег стоит за одной строкой разреза, с честной оговоркой. Если у части позиций
 * себестоимости нет, сумма ЗАНИЖЕНА, и экран обязан сказать это числом, а не умолчать.
 */
export function недосчёт(всего: number, безЦены: number): string {
  if (!безЦены) return ''
  const доля = всего ? Math.round((100 * безЦены) / всего) : 100
  return `${безЦены} из ${всего} позиций без себестоимости (${доля} %) — сумма занижена`
}
